import React, { ReactElement, useEffect, useRef, ReactNode, useState } from "react";
import { createTheme, List, ListItem, SvgIcon, ThemeProvider, Tooltip, Typography } from "@mui/material";
import styles from "./listEditor.module.css";
import { CoreButton, CoreIconButton, CoreTextField } from "../../../core";
import { useMobileLayout, useOverflow } from "../../../hooks";
import Delete from "../../images/x-circle.svg?react";
import noWeb from "../../images/no-websites.svg";
import i18nPackage from "../../../i18n/i18n";
import { dialogViewTheme } from "../../../theme";
import PassIcon from "../../images/check-pass.svg?react";
import FailIcon from "../../images/check-fail.svg?react";

export type ListEditTableProps = {
	currList: string[];
	updateList: (list: string[]) => void;
	placeholder?: string;
	emptyText: string;
};

export function ListEditTableDescription({ children }: { children: ReactNode }) {
	return <div className={styles.tableDescription}>{children}</div>;
}

function EditTableRow({ name, onDelete, children }: { name: string; onDelete: () => void; children: ReactNode }) {
	return (
		<ListItem
			className={styles.tableEntry}
			secondaryAction={
				<CoreIconButton
					variant="plain"
					onClick={onDelete}
					className={styles.deleteButton}
					aria-label={i18nPackage.t("editQuizPage.configurationStep.urlAllowBlockListsModal.deleteAriaLabel", {
						name: name,
					})}
				>
					<SvgIcon>
						<Delete />
					</SvgIcon>
				</CoreIconButton>
			}
		>
			{children}
		</ListItem>
	);
}

export function EllipsisTooltip({
	children,
	entry = children,
	width,
	offset,
	arrowOffset,
}: {
	children: ReactElement;
	entry?: ReactNode;
	width?: number;
	offset?: [number, number];
	arrowOffset?: [number, number];
}) {
	const [textRef, isOverflowed] = useOverflow();
	const measureRef = useRef<HTMLDivElement>(null);
	const [isTall, setIsTall] = useState(false);
	const isMobileLayout = useMobileLayout();
	const localTheme = createTheme(dialogViewTheme);

	useEffect(() => {
		if (measureRef.current) {
			setIsTall(measureRef.current.scrollHeight > 47);
		}
	}, [entry]);

	const childWithRef = React.cloneElement(children, {
		ref: textRef,
		className: `${children.props.className ?? ""} ${styles.ellipsis}`.trim(),
	});

	return (
		<ThemeProvider theme={localTheme}>
			{/* Hidden div to measure height */}
			<div
				ref={measureRef}
				style={{
					visibility: "hidden",
					position: "fixed",
					whiteSpace: "pre-wrap",
					wordBreak: "break-word",
					width: isMobileLayout ? undefined : `${width ?? 500}px`,
					maxWidth: isMobileLayout ? "100%" : undefined,
					fontSize: "13px",
					fontWeight: 500,
					lineHeight: "normal",
					pointerEvents: "none",
				}}
			>
				{entry}
			</div>

			{isOverflowed ? (
				<Tooltip
					title={<div className={styles.tooltipScrollContent}>{entry}</div>}
					arrow
					placement="top"
					classes={{
						tooltip: isTall ? styles.customTooltipLong : styles.customTooltip,
					}}
					PopperProps={{
						disablePortal: true,
						modifiers: [
							{ name: "flip", enabled: false },
							{ name: "preventOverflow", enabled: false },
							{
								name: "offset",
								options: { offset: offset ?? [0, 0] },
							},
						],
					}}
					componentsProps={{
						tooltip: {
							style: { width: `${width ?? 500}px` },
						},
						arrow: {
							style: arrowOffset ? { marginLeft: `${arrowOffset[0]}px`, marginTop: `${arrowOffset[1]}px` } : {},
						},
					}}
				>
					{childWithRef}
				</Tooltip>
			) : (
				childWithRef
			)}
		</ThemeProvider>
	);
}

export function ListEditTable({
	currList,
	updateList,
	emptyText,
}: Pick<ListEditTableProps, "currList" | "updateList" | "emptyText">) {
	const isMobileLayout = useMobileLayout();

	return currList.length === 0 ? (
		<div className={`${styles.horiItems} ${isMobileLayout ? styles.horiItemsCompact : ""}`}>
			<img src={noWeb} role="presentation" alt="" />
			<div className={styles.emptyText}>{emptyText}</div>
		</div>
	) : (
		<div className={`${styles.tableWrapper} ${isMobileLayout ? styles.tableWrapperCompact : ""}`}>
			<List className={styles.table}>
				{currList.map((entry: string, key) => (
					<EditTableRow name={entry} key={key} onDelete={() => updateList(currList.filter((e) => e !== entry))}>
						<EllipsisTooltip>
							<span className={styles.ellipsisLink}>{entry}</span>
						</EllipsisTooltip>
					</EditTableRow>
				))}
			</List>
		</div>
	);
}

type AssessmentResourceURLValidation = {
	noSpaces: boolean;
	hasHttp: boolean;
	hasDomain: boolean;
};

function validateAssessmentResourceUrl(url: string): AssessmentResourceURLValidation {
	const isValidDomain = (url: string) => {
		const urlWithoutProtocol = url.replace(/^[a-zA-Z]+(?:[:/])*/, "");
		try {
			const { hostname } = new URL("https://" + urlWithoutProtocol);
			return /\.[a-zA-Z]{2,}$/.test(hostname);
		} catch (error) {
			return false;
		}
	};
	return {
		noSpaces: !url.includes(" "),
		hasHttp: url.startsWith("http://") || url.startsWith("https://"),
		hasDomain: isValidDomain(url),
	};
}

function ValidationRow({
	pass,
	validationKey,
}: {
	pass: boolean;
	validationKey: keyof AssessmentResourceURLValidation;
}) {
	return (
		<div className={styles.validationRow}>
			{pass ? (
				<PassIcon
					role="img"
					aria-label={i18nPackage.t("editQuizPage.configurationStep.urlAllowBlockListsModal.pass")}
				/>
			) : (
				<FailIcon
					role="img"
					aria-label={i18nPackage.t("editQuizPage.configurationStep.urlAllowBlockListsModal.fail")}
				/>
			)}
			<Typography component={"span"}>
				{i18nPackage.t(`editQuizPage.configurationStep.urlAllowBlockListsModal.url.${validationKey}`)}
			</Typography>
		</div>
	);
}

const hasNonWhitespace = (str: string | null | undefined) => typeof str === "string" && /\S/.test(str);

export default function ListEditor({ currList, updateList, placeholder, emptyText }: ListEditTableProps) {
	const [newEntryInput, setNewEntryInput] = useState("");
	const [urlValidation, setUrlValidation] = useState<AssessmentResourceURLValidation>({
		hasDomain: false,
		hasHttp: false,
		noSpaces: false,
	});
	const isMobileLayout = useMobileLayout();
	const allValidationsPassed = Object.values(urlValidation).every((v) => v);

	const handleAddUrl = () => {
		if (newEntryInput.trim() && allValidationsPassed) {
			updateList(currList.concat([newEntryInput.trim()]));
			setNewEntryInput("");
		}
	};

	return (
		<div className={styles.urlAllowBlockContainer}>
			<div className={`${styles.inputRow} ${isMobileLayout ? styles.inputRowCompact : ""}`}>
				<CoreTextField
					className={styles.inputField}
					value={newEntryInput}
					autoComplete="off"
					label={placeholder}
					onChange={(event) => {
						const url = event.currentTarget.value;
						setNewEntryInput(url);
						setUrlValidation(validateAssessmentResourceUrl(url));
					}}
					size="small"
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							handleAddUrl();
						}
					}}
				/>
				<CoreButton
					onClick={handleAddUrl}
					className={styles.addWebsiteButton}
					disabled={!allValidationsPassed || !newEntryInput.trim()}
				>
					{i18nPackage.t("editQuizPage.configurationStep.urlAllowBlockListsModal.add")}
				</CoreButton>
			</div>
			{hasNonWhitespace(newEntryInput) &&
				(allValidationsPassed ? (
					<div className={styles.validUrlText}>
						<PassIcon />
						<Typography component={"span"}>
							{i18nPackage.t("editQuizPage.configurationStep.urlAllowBlockListsModal.url.validUrl")}
						</Typography>
					</div>
				) : (
					<div className={styles.validationContainer}>
						{(Object.keys(urlValidation) as Array<keyof AssessmentResourceURLValidation>).map((validationKey) => (
							<ValidationRow key={validationKey} validationKey={validationKey} pass={urlValidation[validationKey]} />
						))}
					</div>
				))}
			<ListEditTable currList={currList} updateList={updateList} emptyText={emptyText} />
		</div>
	);
}
